import { Injectable } from '@angular/core';
import { CaseSortCriteria, CaseListFilter } from '@telespot/domain';
import { map } from 'rxjs/operators';
import { PaginatedState } from '@shared/ui';
import { PaginatedStateModel } from '@shared/ui';

export type WorkspaceCaseState = 'pending' | 'read' | 'inProgress' | 'analyzed';

export interface WorkspaceCaseUserData {
  id: string;
  username: string;
}

export interface WorkspaceCaseData {
  id: string;
  name: string;
  encrypted: boolean;
  createdAt: Date;
  updatedAt: Date;
  createdById: string;
  state?: WorkspaceCaseState;
  createdBy?: WorkspaceCaseUserData;
  analysing?: WorkspaceCaseUserData[];
  analyzed?: WorkspaceCaseUserData[];
}

export interface WorkspacePaginationState {
  workspaceId: string;
  previousPageTokens: ({ id: string; name?: string; createdAt: string } | undefined)[];
  pageSize: number;
  sortCriteria?: CaseSortCriteria;
}

export interface WorkspaceCasesStateModel extends PaginatedStateModel<WorkspaceCaseData> {
  workspaceId: string | undefined;
  currentUserId: string | undefined;
  sortCriteria: CaseSortCriteria;
  filters: CaseListFilter[];
  movingCase: boolean;
  copyingCase: boolean;
  encrypted: boolean;
  nameEncrypted: boolean;
  organizationId: string | undefined;
  decryptingCases: boolean;
  canDecrypt: boolean;
  userDecrypted: boolean;
  /** True when every case on the current page is creator-owned (the backend returns those already decrypted), so the decryption toggle is a no-op. */
  alwaysDecrypted: boolean;
  operationError: string | undefined;
  initialPageSize?: number;
  initialPreviousTokens?: (WorkspaceCaseData | undefined)[];
}

export const DEFAULT_CASE_SORT_CRITERIA: CaseSortCriteria = { createdAt: 'desc' };

const initialWorkspaceCasesState: Partial<WorkspaceCasesStateModel> = {
  workspaceId: undefined,
  currentUserId: undefined,
  sortCriteria: { ...DEFAULT_CASE_SORT_CRITERIA },
  filters: [],
  movingCase: false,
  copyingCase: false,
  encrypted: false,
  nameEncrypted: false,
  organizationId: undefined,
  decryptingCases: false,
  canDecrypt: false,
  userDecrypted: false,
  alwaysDecrypted: false,
  operationError: undefined,
};

@Injectable()
export class WorkspaceCasesState extends PaginatedState<WorkspaceCaseData, WorkspaceCasesStateModel> {
  constructor() {
    super(undefined, initialWorkspaceCasesState);
  }

  // ============================================================================
  // Custom Selectors
  // ============================================================================

  public readonly workspaceId$ = this.select((state) => state.workspaceId);
  public readonly currentUserId$ = this.select((state) => state.currentUserId);
  public readonly sortCriteria$ = this.select((state) => state.sortCriteria);
  public readonly filters$ = this.select((state) => state.filters);
  public readonly movingCase$ = this.select((state) => state.movingCase);
  public readonly copyingCase$ = this.select((state) => state.copyingCase);
  public readonly encrypted$ = this.select((state) => state.encrypted);
  public readonly nameEncrypted$ = this.select((state) => state.nameEncrypted);
  public readonly organizationId$ = this.select((state) => state.organizationId);
  public readonly decryptingCases$ = this.select((state) => state.decryptingCases);
  public readonly canDecrypt$ = this.select((state) => state.canDecrypt);
  public readonly userDecrypted$ = this.select((state) => state.userDecrypted);
  public readonly alwaysDecrypted$ = this.select((state) => state.alwaysDecrypted);
  public readonly operationError$ = this.select((state) => state.operationError);
  public readonly initialPageSize$ = this.select((state) => state.initialPageSize);
  public readonly initialPreviousTokens$ = this.select((state) => state.initialPreviousTokens);

  public readonly cases$ = this.items$;
  public readonly totalCaseCount$ = this.totalCount$;

  public readonly decrypted$ = this.cases$.pipe(map((cases) => cases.length > 0 && cases.every((c) => !c.encrypted)));

  // ============================================================================
  // Custom Updaters
  // ============================================================================

  // Also resets alwaysDecrypted: the flag is recomputed per listed page of the workspace,
  // so a workspace switch must not carry a stale `true` that would silently disable the decryption toggle.
  public readonly setWorkspaceId = this.updater(
    (state, workspaceId: string | undefined) =>
      ({ ...state, workspaceId, alwaysDecrypted: false } as WorkspaceCasesStateModel)
  );

  public readonly setCurrentUserId = this.updater(
    (state, currentUserId: string | undefined) => ({ ...state, currentUserId } as WorkspaceCasesStateModel)
  );

  public readonly setSortCriteria = this.updater(
    (state, sortCriteria: CaseSortCriteria) => ({ ...state, sortCriteria } as WorkspaceCasesStateModel)
  );

  public readonly addFilter = this.updater(
    (state, filterItem: CaseListFilter) =>
      ({
        ...state,
        filters: [...state.filters.filter((f) => f.field !== filterItem.field), filterItem],
      } as WorkspaceCasesStateModel)
  );

  public readonly removeFilter = this.updater(
    (state, field: string) =>
      ({
        ...state,
        filters: state.filters.filter((f) => f.field !== field),
      } as WorkspaceCasesStateModel)
  );

  public readonly resetFiltersAndSort = this.updater(
    (state) =>
      ({
        ...state,
        sortCriteria: { ...DEFAULT_CASE_SORT_CRITERIA },
        filters: [],
      } as WorkspaceCasesStateModel)
  );

  public readonly setMovingCase = this.updater(
    (state, movingCase: boolean) => ({ ...state, movingCase } as WorkspaceCasesStateModel)
  );

  public readonly setCopyingCase = this.updater(
    (state, copyingCase: boolean) => ({ ...state, copyingCase } as WorkspaceCasesStateModel)
  );

  public readonly setEncrypted = this.updater(
    (state, encrypted: boolean) => ({ ...state, encrypted } as WorkspaceCasesStateModel)
  );

  public readonly setNameEncrypted = this.updater(
    (state, nameEncrypted: boolean) => ({ ...state, nameEncrypted } as WorkspaceCasesStateModel)
  );

  public readonly setOrganizationId = this.updater(
    (state, organizationId: string | undefined) => ({ ...state, organizationId } as WorkspaceCasesStateModel)
  );

  public readonly setDecryptingCases = this.updater(
    (state, decryptingCases: boolean) => ({ ...state, decryptingCases } as WorkspaceCasesStateModel)
  );

  public readonly setCanDecrypt = this.updater(
    (state, canDecrypt: boolean) => ({ ...state, canDecrypt } as WorkspaceCasesStateModel)
  );

  public readonly setUserDecrypted = this.updater(
    (state, userDecrypted: boolean) => ({ ...state, userDecrypted } as WorkspaceCasesStateModel)
  );

  public readonly setAlwaysDecrypted = this.updater(
    (state, alwaysDecrypted: boolean) => ({ ...state, alwaysDecrypted } as WorkspaceCasesStateModel)
  );

  public readonly setOperationError = this.updater(
    (state, operationError: string | undefined) => ({ ...state, operationError } as WorkspaceCasesStateModel)
  );

  public readonly updateCaseFields = this.updater(
    (state, update: { id: string; changes: Partial<WorkspaceCaseData> }) =>
      ({
        ...state,
        items: this.itemsAdapter.updateOne({ id: update.id, changes: update.changes }, state.items),
      } as WorkspaceCasesStateModel)
  );

  /** Drops every loaded case and resets pagination so the list can be re-fetched from page one. */
  public readonly resetItemsForReload = this.updater(
    (state) =>
      ({
        ...state,
        loading: true,
        items: this.itemsAdapter.removeAll(state.items),
        hasMoreItems: true,
        previousPageTokens: [],
        nextToken: undefined,
      } as WorkspaceCasesStateModel)
  );

  public readonly updateCasesFields = this.updater(
    (state, updates: Array<{ id: string; changes: Partial<WorkspaceCaseData> }>) => {
      if (updates.length === 0) {
        return state;
      }

      return {
        ...state,
        items: updates.reduce(
          (items, { id, changes }) => this.itemsAdapter.updateOne({ id, changes }, items),
          state.items
        ),
      } as WorkspaceCasesStateModel;
    }
  );

  public readonly setInitialPagination = this.updater(
    (
      state,
      params: {
        pageSize?: number;
        previousTokens?: (WorkspaceCaseData | undefined)[];
      }
    ) =>
      ({
        ...state,
        initialPageSize: params.pageSize,
        initialPreviousTokens: params.previousTokens,
      } as WorkspaceCasesStateModel)
  );

  public readonly clearInitialPagination = this.updater(
    (state) =>
      ({
        ...state,
        initialPageSize: undefined,
        initialPreviousTokens: undefined,
      } as WorkspaceCasesStateModel)
  );
}
